import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { 
  CheckCircle, 
  Clock, 
  Users, 
  TrendingUp, 
  Star,
  ArrowRight,
  MessageSquare,
  BarChart3,
  Shield,
  Zap,
  Play,
  DollarSign,
  Target,
  Award,
  Rocket,
  TrendingDown,
  CheckCheck,
  LogIn,
  Smartphone,
  UserPlus,
  Send
} from 'lucide-react'
import ThemeToggle from '../components/ui/ThemeToggle'

const LandingPage = () => {
  const [showVideo, setShowVideo] = useState(false)

  return (
    <div className="min-h-screen bg-white dark:bg-dark-bg-primary transition-colors">
      {/* Header */}
      <header className="bg-white dark:bg-dark-bg-primary shadow-sm border-b border-gray-100 dark:border-dark-border-primary transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg">C</span>
              </div>
              <span className="ml-3 text-xl font-bold text-gray-900 dark:text-dark-text-primary">Cotação.ai</span>
            </div>
            <nav className="hidden md:flex space-x-8">
              <a href="#funcionalidades" className="text-gray-600 dark:text-dark-text-secondary hover:text-green-500 dark:hover:text-dark-accent-green transition-colors">
                Funcionalidades
              </a>
              <a href="#depoimentos" className="text-gray-600 dark:text-dark-text-secondary hover:text-green-500 dark:hover:text-dark-accent-green transition-colors">
                Depoimentos
              </a>
              <a href="#precos" className="text-gray-600 dark:text-dark-text-secondary hover:text-green-500 dark:hover:text-dark-accent-green transition-colors">
                Preços
              </a>
            </nav>
            <div className="flex items-center space-x-4">
              <ThemeToggle />
              <Link 
                to="/login" 
                className="bg-green-500 text-white px-6 py-2 rounded-lg hover:bg-green-500/90 transition-colors font-medium"
              >
                Entrar
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section com Vídeo */}
      <section className="bg-gradient-to-br from-green-500/5 to-white dark:from-dark-bg-secondary dark:to-dark-bg-primary py-20 transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Texto */}
            <div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 dark:text-dark-text-primary mb-6">
                De 6 horas para{' '}
                <span className="text-green-500 dark:text-dark-accent-green">15 minutos</span>{' '}
                em cotações
              </h1>
              <p className="text-xl text-gray-600 dark:text-dark-text-secondary mb-8">
                Economize 28% nos custos de compras com automação inteligente 100% via WhatsApp. 
                ROI de 380% no primeiro ano.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="https://pay.kiwify.com.br/hE8a43T"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-green-500 text-white px-8 py-4 rounded-lg hover:bg-green-500/90 transition-colors font-medium text-lg inline-flex items-center justify-center"
                >
                  Começar Agora - R$ 99
                  <ArrowRight className="ml-2 w-5 h-5" />
                </a>
                <a
                  href="https://calendar.google.com/calendar/u/0/appointments/schedules/AcZssZ0A-72dco-q-NgB3om1MIch2Vt0IZ674R4zFoIychuHVpeayT7bZINoa71lYW6zlQgzzLHnbvyW"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="border-2 border-green-500 text-green-500 dark:text-dark-accent-green px-8 py-4 rounded-lg hover:bg-green-500/5 dark:hover:bg-dark-bg-tertiary transition-colors font-medium text-lg inline-flex items-center justify-center"
                >
                  <Play className="mr-2 w-5 h-5" />
                  Solicitar sua demonstração
                </a>
              </div>
            </div>

            {/* Vídeo */}
            <div className="relative">
              <div className="aspect-video bg-gray-900 rounded-xl shadow-2xl overflow-hidden">
                {!showVideo ? (
                  <div 
                    className="w-full h-full flex items-center justify-center cursor-pointer group"
                    onClick={() => setShowVideo(true)}
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-green-500/20 to-blue-500/20"></div>
                    <div className="relative z-10 text-center">
                      <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                        <Play className="w-10 h-10 text-green-500 ml-1" />
                      </div>
                      <p className="text-white font-semibold text-lg">Assista ao vídeo de apresentação</p>
                    </div>
                  </div>
                ) : (
                  <iframe
                    className="w-full h-full"
                    src="https://www.youtube.com/embed/SEU_VIDEO_ID?autoplay=1"
                    title="Vídeo de Vendas Cotação.ai"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                )}
              </div>
              <p className="text-sm text-gray-500 dark:text-dark-text-muted text-center mt-4">
                ⚡ Veja como economizar 28% nas compras em apenas 15 minutos
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white dark:bg-dark-bg-primary transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-8 text-center">
            <div>
              <div className="text-3xl font-bold text-green-500 dark:text-dark-accent-green mb-2">15min</div>
              <div className="text-gray-600 dark:text-dark-text-secondary">Tempo de cotação</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-green-500 dark:text-dark-accent-green mb-2">28%</div>
              <div className="text-gray-600 dark:text-dark-text-secondary">Economia comprovada</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-green-500 dark:text-dark-accent-green mb-2">380%</div>
              <div className="text-gray-600 dark:text-dark-text-secondary">ROI no 1º ano</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-green-500 dark:text-dark-accent-green mb-2">24h</div>
              <div className="text-gray-600 dark:text-dark-text-secondary">Implementação</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-green-500 dark:text-dark-accent-green mb-2">847</div>
              <div className="text-gray-600 dark:text-dark-text-secondary">Clientes ativos</div>
            </div>
          </div>
        </div>
      </section>

      {/* Como Funciona */}
      <section className="py-20 bg-white dark:bg-dark-bg-primary transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-3xl md:text-4xl font-bold text-[#252525] dark:text-dark-text-primary mb-4">
              Como funciona o processo dentro da Cotação AI
            </h2>
            <p className="text-xl text-gray-600 dark:text-dark-text-secondary max-w-3xl mx-auto">
              Em 5 passos simples, você transforma seu processo de cotações
            </p>
          </div>

          <div className="space-y-24">
            {/* Passo 1 - Texto à esquerda, Vídeo à direita */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <div className="inline-block bg-[#E9EAEB] dark:bg-dark-bg-tertiary px-4 py-2 rounded-full">
                  <span className="text-sm font-semibold text-[#597445] dark:text-dark-accent-green">Passo 1</span>
                </div>
                <h3 className="text-3xl font-bold text-[#252525] dark:text-dark-text-primary">
                  Você acessa o Cotação AI
                </h3>
                <p className="text-lg text-gray-600 dark:text-dark-text-secondary leading-relaxed">
                  Faz o login normalmente na plataforma e conecta o seu número de WhatsApp ao sistema.
                </p>
                <a
                  href="https://pay.kiwify.com.br/hE8a43T"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center bg-[#84BA42] hover:bg-[#597445] text-white px-8 py-3 rounded-lg transition-colors font-semibold"
                >
                  Acessar a plataforma
                  <ArrowRight className="ml-2 w-5 h-5" />
                </a>
              </div>
              <div className="bg-[#E9EAEB] dark:bg-dark-bg-elevated rounded-2xl overflow-hidden shadow-lg aspect-video flex items-center justify-center">
                <div className="text-center p-8">
                  <Play className="w-16 h-16 text-[#84BA42] mx-auto mb-4" />
                  <p className="text-gray-600 dark:text-dark-text-secondary">Dashboard da Cotação AI</p>
                </div>
              </div>
            </div>

            {/* Passo 2 - Vídeo à esquerda, Texto à direita */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="bg-[#E9EAEB] dark:bg-dark-bg-elevated rounded-2xl overflow-hidden shadow-lg aspect-video flex items-center justify-center lg:order-1">
                <div className="text-center p-8">
                  <Smartphone className="w-16 h-16 text-[#84BA42] mx-auto mb-4" />
                  <p className="text-gray-600 dark:text-dark-text-secondary">Escanear QR Code</p>
                </div>
              </div>
              <div className="space-y-6 lg:order-2">
                <div className="inline-block bg-[#E9EAEB] dark:bg-dark-bg-tertiary px-4 py-2 rounded-full">
                  <span className="text-sm font-semibold text-[#597445] dark:text-dark-accent-green">Passo 2</span>
                </div>
                <h3 className="text-3xl font-bold text-[#252525] dark:text-dark-text-primary">
                  Ativa o WhatsApp integrado
                </h3>
                <p className="text-lg text-gray-600 dark:text-dark-text-secondary leading-relaxed">
                  Basta escanear o QR Code e pronto — seu WhatsApp fica sincronizado com o Cotação AI para envio automático de cotações.
                </p>
                <a
                  href="https://pay.kiwify.com.br/hE8a43T"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center bg-[#84BA42] hover:bg-[#597445] text-white px-8 py-3 rounded-lg transition-colors font-semibold"
                >
                  Ativar WhatsApp
                  <ArrowRight className="ml-2 w-5 h-5" />
                </a>
              </div>
            </div>

            {/* Passo 3 - Texto à esquerda, Vídeo à direita */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <div className="inline-block bg-[#E9EAEB] dark:bg-dark-bg-tertiary px-4 py-2 rounded-full">
                  <span className="text-sm font-semibold text-[#597445] dark:text-dark-accent-green">Passo 3</span>
                </div>
                <h3 className="text-3xl font-bold text-[#252525] dark:text-dark-text-primary">
                  Cadastra seus fornecedores
                </h3>
                <p className="text-lg text-gray-600 dark:text-dark-text-secondary leading-relaxed">
                  Inclui nome, categoria, telefone/WhatsApp e quaisquer informações relevantes. Isso é importante para que a IA saiba exatamente para quem enviar cada item.
                </p>
                <a
                  href="https://pay.kiwify.com.br/hE8a43T"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center bg-[#84BA42] hover:bg-[#597445] text-white px-8 py-3 rounded-lg transition-colors font-semibold"
                >
                  Cadastrar fornecedores
                  <ArrowRight className="ml-2 w-5 h-5" />
                </a>
              </div>
              <div className="bg-[#E9EAEB] dark:bg-dark-bg-elevated rounded-2xl overflow-hidden shadow-lg aspect-video flex items-center justify-center">
                <div className="text-center p-8">
                  <UserPlus className="w-16 h-16 text-[#84BA42] mx-auto mb-4" />
                  <p className="text-gray-600 dark:text-dark-text-secondary">Lista de fornecedores</p>
                </div>
              </div>
            </div>

            {/* Passo 4 - Vídeo à esquerda, Texto à direita */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="bg-[#E9EAEB] dark:bg-dark-bg-elevated rounded-2xl overflow-hidden shadow-lg aspect-video flex items-center justify-center lg:order-1">
                <div className="text-center p-8">
                  <Send className="w-16 h-16 text-[#84BA42] mx-auto mb-4" />
                  <p className="text-gray-600 dark:text-dark-text-secondary">Fluxo de cotação</p>
                </div>
              </div>
              <div className="space-y-6 lg:order-2">
                <div className="inline-block bg-[#E9EAEB] dark:bg-dark-bg-tertiary px-4 py-2 rounded-full">
                  <span className="text-sm font-semibold text-[#597445] dark:text-dark-accent-green">Passo 4</span>
                </div>
                <h3 className="text-3xl font-bold text-[#252525] dark:text-dark-text-primary">
                  Inicia uma cotação
                </h3>
                <p className="text-lg text-gray-600 dark:text-dark-text-secondary leading-relaxed">
                  Você envia sua lista de produtos → o sistema categoriza → dispara para os fornecedores corretos → consolida tudo e te entrega pronto para decidir.
                </p>
                <a
                  href="https://pay.kiwify.com.br/hE8a43T"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center bg-[#84BA42] hover:bg-[#597445] text-white px-8 py-3 rounded-lg transition-colors font-semibold"
                >
                  Começar uma cotação
                  <ArrowRight className="ml-2 w-5 h-5" />
                </a>
              </div>
            </div>

            {/* Passo 5 - Texto à esquerda, Vídeo à direita */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <div className="inline-block bg-[#E9EAEB] dark:bg-dark-bg-tertiary px-4 py-2 rounded-full">
                  <span className="text-sm font-semibold text-[#597445] dark:text-dark-accent-green">Passo 5</span>
                </div>
                <h3 className="text-3xl font-bold text-[#252525] dark:text-dark-text-primary">
                  Recebe o resumo completo
                </h3>
                <p className="text-lg text-gray-600 dark:text-dark-text-secondary leading-relaxed">
                  O sistema consolida todas as respostas dos fornecedores e entrega um resumo comparativo completo, com preços, prazos e condições organizados. Você visualiza tudo em uma única tela e toma a melhor decisão em segundos.
                </p>
                <a
                  href="https://calendar.google.com/calendar/u/0/appointments/schedules/AcZssZ0A-72dco-q-NgB3om1MIch2Vt0IZ674R4zFoIychuHVpeayT7bZINoa71lYW6zlQgzzLHnbvyW"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center bg-white border-2 border-[#84BA42] text-[#84BA42] hover:bg-[#84BA42] hover:text-white px-8 py-3 rounded-lg transition-colors font-semibold"
                >
                  Ver demonstração
                  <ArrowRight className="ml-2 w-5 h-5" />
                </a>
              </div>
              <div className="bg-[#E9EAEB] dark:bg-dark-bg-elevated rounded-2xl overflow-hidden shadow-lg aspect-video flex items-center justify-center">
                <div className="text-center p-8">
                  <BarChart3 className="w-16 h-16 text-[#84BA42] mx-auto mb-4" />
                  <p className="text-gray-600 dark:text-dark-text-secondary">Resumo comparativo</p>
                </div>
              </div>
            </div>
          </div>

          {/* CTA Final */}
          <div className="mt-20 text-center">
            <p className="text-lg text-gray-600 dark:text-dark-text-secondary mb-6">
              Simples assim! Comece hoje e veja a diferença em minutos.
            </p>
            <a
              href="https://pay.kiwify.com.br/hE8a43T"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center bg-[#84BA42] hover:bg-[#597445] text-white px-10 py-4 rounded-lg transition-colors font-bold text-lg shadow-lg"
            >
              Começar Agora - R$ 99
              <ArrowRight className="ml-3 w-5 h-5" />
            </a>
          </div>
        </div>
      </section>

      {/* Seção de Benefícios Detalhados */}
      <section className="py-20 bg-gray-50 dark:bg-dark-bg-secondary transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#252525] dark:text-dark-text-primary mb-4">
              Por que COTAÇÃO.AI é a escolha certa para seu negócio
            </h2>
            <p className="text-xl text-gray-600 dark:text-dark-text-secondary max-w-3xl mx-auto">
              Resultados comprovados que transformam operações de food service
            </p>
          </div>

          <div className="space-y-16">
            {/* Bloco 1: Resultados Comprovados */}
            <div className="bg-gradient-to-br from-[#84BA42]/10 to-white dark:from-dark-bg-elevated dark:to-dark-bg-primary p-12 rounded-2xl border border-[#84BA42]/20">
              <div className="text-center mb-12">
                <div className="inline-block bg-[#84BA42] text-white px-6 py-2 rounded-full mb-6">
                  <span className="font-bold">Resultados Comprovados</span>
                </div>
                <h3 className="text-3xl font-bold text-[#252525] dark:text-dark-text-primary mb-4">
                  Transforme suas cotações em minutos
                </h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="text-center">
                  <div className="w-20 h-20 bg-[#E9EAEB] dark:bg-dark-bg-tertiary rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Clock className="w-10 h-10 text-[#84BA42]" />
                  </div>
                  <div className="text-4xl font-bold text-[#84BA42] mb-2">15min</div>
                  <p className="text-gray-600 dark:text-dark-text-secondary">Tempo médio de cotação</p>
                  <p className="text-sm text-gray-500 dark:text-dark-text-muted mt-2">Antes: 6 horas</p>
                </div>
                
                <div className="text-center">
                  <div className="w-20 h-20 bg-[#E9EAEB] dark:bg-dark-bg-tertiary rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <TrendingUp className="w-10 h-10 text-[#84BA42]" />
                  </div>
                  <div className="text-4xl font-bold text-[#84BA42] mb-2">28%</div>
                  <p className="text-gray-600 dark:text-dark-text-secondary">Economia comprovada</p>
                  <p className="text-sm text-gray-500 dark:text-dark-text-muted mt-2">Nos custos de compras</p>
                </div>
                
                <div className="text-center">
                  <div className="w-20 h-20 bg-[#E9EAEB] dark:bg-dark-bg-tertiary rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Rocket className="w-10 h-10 text-[#84BA42]" />
                  </div>
                  <div className="text-4xl font-bold text-[#84BA42] mb-2">380%</div>
                  <p className="text-gray-600 dark:text-dark-text-secondary">ROI no primeiro ano</p>
                  <p className="text-sm text-gray-500 dark:text-dark-text-muted mt-2">Retorno garantido</p>
                </div>
              </div>
            </div>

            {/* Bloco 2: Como Funciona (Simplificado) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-white dark:bg-dark-bg-elevated p-8 rounded-2xl shadow-sm border border-gray-100 dark:border-dark-border-primary">
                <div className="flex items-start mb-6">
                  <div className="w-12 h-12 bg-[#84BA42]/10 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                    <Zap className="w-6 h-6 text-[#84BA42]" />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-[#252525] dark:text-dark-text-primary mb-3">
                      Automação Inteligente
                    </h4>
                    <ul className="space-y-3">
                      <li className="flex items-start text-gray-600 dark:text-dark-text-secondary">
                        <CheckCircle className="w-5 h-5 text-[#84BA42] mr-2 mt-0.5 flex-shrink-0" />
                        <span>Disparo simultâneo para até 50 fornecedores</span>
                      </li>
                      <li className="flex items-start text-gray-600 dark:text-dark-text-secondary">
                        <CheckCircle className="w-5 h-5 text-[#84BA42] mr-2 mt-0.5 flex-shrink-0" />
                        <span>Sistema de scoring com 15 critérios</span>
                      </li>
                      <li className="flex items-start text-gray-600 dark:text-dark-text-secondary">
                        <CheckCircle className="w-5 h-5 text-[#84BA42] mr-2 mt-0.5 flex-shrink-0" />
                        <span>Histórico completo para análise</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-dark-bg-elevated p-8 rounded-2xl shadow-sm border border-gray-100 dark:border-dark-border-primary">
                <div className="flex items-start mb-6">
                  <div className="w-12 h-12 bg-[#84BA42]/10 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                    <Shield className="w-6 h-6 text-[#84BA42]" />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-[#252525] dark:text-dark-text-primary mb-3">
                      Implementação Simples
                    </h4>
                    <ul className="space-y-3">
                      <li className="flex items-start text-gray-600 dark:text-dark-text-secondary">
                        <CheckCircle className="w-5 h-5 text-[#84BA42] mr-2 mt-0.5 flex-shrink-0" />
                        <span>100% via WhatsApp que você já usa</span>
                      </li>
                      <li className="flex items-start text-gray-600 dark:text-dark-text-secondary">
                        <CheckCircle className="w-5 h-5 text-[#84BA42] mr-2 mt-0.5 flex-shrink-0" />
                        <span>Pronto em 24 horas</span>
                      </li>
                      <li className="flex items-start text-gray-600 dark:text-dark-text-secondary">
                        <CheckCircle className="w-5 h-5 text-[#84BA42] mr-2 mt-0.5 flex-shrink-0" />
                        <span>Sem interromper sua operação</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Bloco 3: CTA com Garantia */}
            <div className="bg-gradient-to-br from-[#84BA42] to-[#597445] p-12 rounded-2xl shadow-xl text-white text-center">
              <div className="max-w-3xl mx-auto">
                <Award className="w-16 h-16 mx-auto mb-6 opacity-90" />
                <h3 className="text-3xl md:text-4xl font-bold mb-4">
                  Garantia de Resultados
                </h3>
                <p className="text-xl mb-8 text-green-50">
                  Junte-se a +300 empresas que já transformaram suas cotações
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
                  <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl">
                    <div className="text-3xl font-bold mb-2">15min</div>
                    <p className="text-sm text-green-100">Tempo de cotação garantido</p>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl">
                    <div className="text-3xl font-bold mb-2">15%</div>
                    <p className="text-sm text-green-100">Economia mínima garantida</p>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl">
                    <div className="text-3xl font-bold mb-2">24h</div>
                    <p className="text-sm text-green-100">Implementação completa</p>
                  </div>
                </div>

                <a
                  href="https://pay.kiwify.com.br/hE8a43T"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center bg-white text-[#597445] px-10 py-5 rounded-xl hover:bg-gray-100 transition-colors font-bold text-xl shadow-2xl"
                >
                  Começar Agora - R$ 99
                  <ArrowRight className="ml-3 w-6 h-6" />
                </a>
                <p className="mt-6 text-green-100 text-sm">
                  ⚡ Ou seu dinheiro de volta • Sem riscos • Suporte dedicado
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="funcionalidades" className="py-20 bg-gray-50 dark:bg-dark-bg-secondary transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-dark-text-primary mb-4">
              Funcionalidades que transformam seu negócio
            </h2>
            <p className="text-xl text-gray-600 dark:text-dark-text-secondary max-w-2xl mx-auto">
              Tudo que você precisa para automatizar e otimizar seu processo de cotações
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white dark:bg-dark-bg-elevated p-8 rounded-xl shadow-sm hover:shadow-md transition-all border border-gray-100 dark:border-dark-border-primary">
              <div className="w-12 h-12 bg-green-500/10 dark:bg-dark-bg-tertiary rounded-lg flex items-center justify-center mb-6">
                <MessageSquare className="w-6 h-6 text-green-500 dark:text-dark-accent-green" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-dark-text-primary mb-4">
                Cotações Automáticas
              </h3>
              <p className="text-gray-600 dark:text-dark-text-secondary">
                Envie solicitações de cotação para múltiplos fornecedores simultaneamente 
                e receba respostas organizadas em tempo real.
              </p>
            </div>

            <div className="bg-white dark:bg-dark-bg-elevated p-8 rounded-xl shadow-sm hover:shadow-md transition-all border border-gray-100 dark:border-dark-border-primary">
              <div className="w-12 h-12 bg-green-500/10 dark:bg-dark-bg-tertiary rounded-lg flex items-center justify-center mb-6">
                <BarChart3 className="w-6 h-6 text-green-500 dark:text-dark-accent-green" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-dark-text-primary mb-4">
                Comparação Inteligente
              </h3>
              <p className="text-gray-600 dark:text-dark-text-secondary">
                Compare preços, prazos e condições de pagamento de forma automática 
                com relatórios detalhados e insights.
              </p>
            </div>

            <div className="bg-white dark:bg-dark-bg-elevated p-8 rounded-xl shadow-sm hover:shadow-md transition-all border border-gray-100 dark:border-dark-border-primary">
              <div className="w-12 h-12 bg-green-500/10 dark:bg-dark-bg-tertiary rounded-lg flex items-center justify-center mb-6">
                <Users className="w-6 h-6 text-green-500 dark:text-dark-accent-green" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-dark-text-primary mb-4">
                Gestão de Fornecedores
              </h3>
              <p className="text-gray-600 dark:text-dark-text-secondary">
                Mantenha uma base completa de fornecedores com histórico de preços, 
                avaliações e performance.
              </p>
            </div>

            <div className="bg-white dark:bg-dark-bg-elevated p-8 rounded-xl shadow-sm hover:shadow-md transition-all border border-gray-100 dark:border-dark-border-primary">
              <div className="w-12 h-12 bg-green-500/10 dark:bg-dark-bg-tertiary rounded-lg flex items-center justify-center mb-6">
                <Clock className="w-6 h-6 text-green-500 dark:text-dark-accent-green" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-dark-text-primary mb-4">
                Respostas em Tempo Real
              </h3>
              <p className="text-gray-600 dark:text-dark-text-secondary">
                Receba notificações instantâneas quando fornecedores respondem 
                suas cotações e tome decisões mais rápidas.
              </p>
            </div>

            <div className="bg-white dark:bg-dark-bg-elevated p-8 rounded-xl shadow-sm hover:shadow-md transition-all border border-gray-100 dark:border-dark-border-primary">
              <div className="w-12 h-12 bg-green-500/10 dark:bg-dark-bg-tertiary rounded-lg flex items-center justify-center mb-6">
                <Shield className="w-6 h-6 text-green-500 dark:text-dark-accent-green" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-dark-text-primary mb-4">
                Segurança Total
              </h3>
              <p className="text-gray-600 dark:text-dark-text-secondary">
                Seus dados e cotações protegidos com criptografia de ponta 
                e backup automático na nuvem.
              </p>
            </div>

            <div className="bg-white dark:bg-dark-bg-elevated p-8 rounded-xl shadow-sm hover:shadow-md transition-all border border-gray-100 dark:border-dark-border-primary">
              <div className="w-12 h-12 bg-green-500/10 dark:bg-dark-bg-tertiary rounded-lg flex items-center justify-center mb-6">
                <Zap className="w-6 h-6 text-green-500 dark:text-dark-accent-green" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-dark-text-primary mb-4">
                Integração Simples
              </h3>
              <p className="text-gray-600 dark:text-dark-text-secondary">
                Conecte com seus sistemas existentes através de APIs 
                e webhooks para uma experiência integrada.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="depoimentos" className="py-20 bg-white dark:bg-dark-bg-primary transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-dark-text-primary mb-4">
              O que nossos clientes conquistaram
            </h2>
            <p className="text-xl text-gray-600 dark:text-dark-text-secondary max-w-2xl mx-auto">
              Empresas que transformaram seus processos de cotação com nossa plataforma
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-gray-50 dark:bg-dark-secondary p-8 rounded-xl transition-colors">
              <div className="flex items-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                ))}
              </div>
              <p className="text-gray-700 dark:text-dark-text-secondary mb-6">
                "Reduziu nosso tempo de cotação em 80%. Agora conseguimos comparar 
                preços de 20 fornecedores em minutos, não em dias."
              </p>
              <div className="flex items-center">
                <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                  <span className="text-white font-semibold">MR</span>
                </div>
                <div className="ml-4">
                  <div className="font-semibold text-gray-900 dark:text-dark-text-primary">Maria Rosa</div>
                  <div className="text-gray-600 dark:text-dark-text-muted">Diretora de Compras - TechCorp</div>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 dark:bg-dark-bg-secondary p-8 rounded-xl transition-colors border border-gray-100 dark:border-dark-border-primary">
              <div className="flex items-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                ))}
              </div>
              <p className="text-gray-700 dark:text-dark-text-secondary mb-6">
                "Economizamos 30% nos custos de compras e melhoramos muito 
                o relacionamento com nossos fornecedores."
              </p>
              <div className="flex items-center">
                <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                  <span className="text-white font-semibold">JS</span>
                </div>
                <div className="ml-4">
                  <div className="font-semibold text-gray-900 dark:text-dark-text-primary">João Silva</div>
                  <div className="text-gray-600 dark:text-dark-text-muted">CEO - Indústria Moderna</div>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 dark:bg-dark-bg-secondary p-8 rounded-xl transition-colors border border-gray-100 dark:border-dark-border-primary">
              <div className="flex items-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                ))}
              </div>
              <p className="text-gray-700 dark:text-dark-text-secondary mb-6">
                "Interface intuitiva e resultados impressionantes. Nossa equipe 
                adotou a ferramenta em poucos dias."
              </p>
              <div className="flex items-center">
                <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                  <span className="text-white font-semibold">AC</span>
                </div>
                <div className="ml-4">
                  <div className="font-semibold text-gray-900 dark:text-dark-text-primary">Ana Costa</div>
                  <div className="text-gray-600 dark:text-dark-text-muted">Gerente de Suprimentos - LogiMax</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="precos" className="py-20 bg-white dark:bg-dark-bg-primary transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-dark-text-primary mb-4">
              Comece com preço especial de lançamento
            </h2>
            <p className="text-xl text-gray-600 dark:text-dark-text-secondary max-w-2xl mx-auto">
              Investimento que se paga em economia no primeiro mês
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-green-500/5 to-white dark:bg-dark-bg-elevated rounded-2xl shadow-2xl overflow-hidden border-2 border-green-500 dark:border-dark-accent-green transition-colors">
              <div className="bg-gradient-to-r from-green-500 to-green-500/90 text-white text-center py-6">
                <div className="inline-block bg-yellow-400 text-gray-900 px-4 py-1 rounded-full text-sm font-bold mb-2">
                  🔥 OFERTA ESPECIAL
                </div>
                <h3 className="text-2xl font-bold">Plano Mensal Recorrente</h3>
              </div>
              
              <div className="p-8 md:p-12">
                <div className="grid md:grid-cols-2 gap-8 mb-8">
                  {/* Primeiro Mês */}
                  <div className="text-center p-6 bg-green-500/5 dark:bg-dark-bg-tertiary rounded-xl border-2 border-green-500">
                    <div className="text-sm font-semibold text-green-500 dark:text-dark-accent-green mb-2">
                      PRIMEIRO MÊS
                    </div>
                    <div className="text-6xl font-bold text-green-500 dark:text-dark-accent-green mb-2">
                      R$ 99
                    </div>
                    <div className="text-gray-600 dark:text-dark-text-secondary text-sm">
                      Preço promocional de entrada
                    </div>
                    <div className="mt-4 inline-block bg-green-500 text-white px-4 py-2 rounded-lg text-sm font-semibold">
                      Economia de R$ 50,90
                    </div>
                  </div>

                  {/* Meses Seguintes */}
                  <div className="text-center p-6 bg-white dark:bg-dark-bg-secondary rounded-xl border-2 border-gray-300 dark:border-dark-border-primary">
                    <div className="text-sm font-semibold text-gray-700 dark:text-dark-text-primary mb-2">
                      MESES SEGUINTES
                    </div>
                    <div className="text-6xl font-bold text-gray-900 dark:text-dark-text-primary mb-2">
                      R$ 149,90
                    </div>
                    <div className="text-gray-600 dark:text-dark-text-secondary text-sm">
                      Valor mensal recorrente
                    </div>
                    <div className="mt-4 text-sm text-gray-600 dark:text-dark-text-secondary">
                      Cancele quando quiser
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 dark:bg-dark-bg-tertiary border border-blue-200 dark:border-dark-border-primary rounded-lg p-6 mb-8">
                  <h4 className="font-bold text-gray-900 dark:text-dark-text-primary mb-4 text-lg">
                    ✨ Tudo incluído em todos os meses:
                  </h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    <ul className="space-y-3">
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-green-500 dark:text-dark-accent-green mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700 dark:text-dark-text-secondary">Cotações ilimitadas</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-green-500 dark:text-dark-accent-green mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700 dark:text-dark-text-secondary">Até 50 fornecedores simultâneos</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-green-500 dark:text-dark-accent-green mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700 dark:text-dark-text-secondary">Sistema de scoring inteligente</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-green-500 dark:text-dark-accent-green mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700 dark:text-dark-text-secondary">Relatórios e análises avançadas</span>
                      </li>
                    </ul>
                    <ul className="space-y-3">
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-green-500 dark:text-dark-accent-green mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700 dark:text-dark-text-secondary">Histórico completo de cotações</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-green-500 dark:text-dark-accent-green mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700 dark:text-dark-text-secondary">Suporte seg-sáb, 8h-20h</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-green-500 dark:text-dark-accent-green mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700 dark:text-dark-text-secondary">Implementação em 24 horas</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-green-500 dark:text-dark-accent-green mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700 dark:text-dark-text-secondary">100% via WhatsApp</span>
                      </li>
                    </ul>
                  </div>
                </div>

                <a
                  href="https://pay.kiwify.com.br/hE8a43T"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full bg-green-500 text-white py-5 rounded-lg hover:bg-green-500/90 transition-all font-bold text-xl inline-flex items-center justify-center shadow-lg hover:shadow-xl transform hover:scale-105"
                >
                  🚀 Começar Agora por R$ 99
                  <ArrowRight className="ml-3 w-6 h-6" />
                </a>

                <div className="mt-6 text-center space-y-2">
                  <p className="text-sm text-gray-600 dark:text-dark-text-secondary">
                    ✅ Garantia de 30 dias ou seu dinheiro de volta
                  </p>
                  <p className="text-sm text-gray-600 dark:text-dark-text-secondary">
                    ✅ Cancele quando quiser, sem multas ou taxas
                  </p>
                  <p className="text-sm font-semibold text-green-500 dark:text-dark-accent-green">
                    ⚡ Economia média de R$ 840/mês em compras
                  </p>
                </div>
              </div>
            </div>

            {/* Calculadora de ROI */}
            <div className="mt-12 bg-gray-50 dark:bg-dark-bg-secondary rounded-xl p-8 border border-gray-200 dark:border-dark-border-primary">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-dark-text-primary mb-6 text-center">
                💰 Seu Retorno Sobre Investimento
              </h3>
              <div className="grid md:grid-cols-3 gap-6 text-center">
                <div>
                  <div className="text-3xl font-bold text-green-500 dark:text-dark-accent-green mb-2">R$ 149,90</div>
                  <div className="text-sm text-gray-600 dark:text-dark-text-secondary">Investimento mensal</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-blue-600 mb-2">R$ 840</div>
                  <div className="text-sm text-gray-600 dark:text-dark-text-secondary">Economia média mensal</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-purple-600 mb-2">560%</div>
                  <div className="text-sm text-gray-600 dark:text-dark-text-secondary">ROI médio mensal</div>
                </div>
              </div>
              <p className="text-center text-sm text-gray-600 dark:text-dark-text-secondary mt-6">
                Baseado em dados reais de 847 clientes ativos
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-green-500 dark:bg-dark-bg-primary transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Pronto para economizar 28% nas suas compras?
          </h2>
          <p className="text-xl text-green-100 mb-8 max-w-2xl mx-auto">
            Junte-se a +300 empresas que já transformaram suas cotações com COTAÇÃO.AI
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a
              href="https://pay.kiwify.com.br/hE8a43T"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white text-green-500 px-10 py-5 rounded-lg hover:bg-gray-100 transition-all font-bold text-xl inline-flex items-center shadow-2xl hover:shadow-3xl transform hover:scale-105"
            >
              🚀 Começar Agora - R$ 99
              <ArrowRight className="ml-3 w-6 h-6" />
            </a>
          </div>
          <p className="mt-6 text-green-100 text-sm">
            ⚡ Implementação em 24h • 💰 Garantia de 30 dias • 🎯 Resultados na primeira semana
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 dark:bg-dark-bg-secondary text-white py-12 transition-colors border-t border-gray-800 dark:border-dark-border-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold">C</span>
                </div>
                <span className="ml-2 text-lg font-bold">Cotação.ai</span>
              </div>
              <p className="text-gray-400 dark:text-dark-text-muted">
                Automatize suas cotações com inteligência artificial
              </p>
              <p className="text-sm text-gray-500 dark:text-dark-text-muted mt-3">
                CNPJ: 63.210.011/0001-16
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold mb-4">Produto</h3>
              <ul className="space-y-2 text-gray-400 dark:text-dark-text-muted">
                <li><a href="#funcionalidades" className="hover:text-white transition-colors">Funcionalidades</a></li>
                <li><a href="#precos" className="hover:text-white transition-colors">Preços</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Integrações</a></li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold mb-4">Empresa</h3>
              <ul className="space-y-2 text-gray-400 dark:text-dark-text-muted">
                <li><a href="#" className="hover:text-white transition-colors">Sobre</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Blog</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Carreiras</a></li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold mb-4">Suporte</h3>
              <ul className="space-y-2 text-gray-400 dark:text-dark-text-muted">
                <li><a href="#" className="hover:text-white transition-colors">Central de Ajuda</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Contato</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Status</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 dark:border-dark-border-primary mt-8 pt-8 text-center text-gray-400 dark:text-dark-text-muted">
            <p>&copy; 2024 Cotação.ai. Todos os direitos reservados.</p>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default LandingPage
